// action - state management
import {
  // eslint-disable-next-line
  REGISTER,
  LOGIN,
  LOGOUT,
  IS_INITIALIZED,
  CHILDREN_IS_INITIALIZED,
  GOOGLE_PLACES_AUTOCOMPLETE_LOADED,
  SET_CURRENT_SOFTWARE_VERSION,
  FETCH_USER_ORG_SUCCESS,
  FETCH_USER_ONBOARDING_OBJECT_SUCCESS,
  FETCH_ORG_PRIVATE_DATA_SUCCESS,
  FETCH_PROVIDER_ORG_TO_ORG_SUBSCRIPTION_TYPES_SUCCESS,
  FETCH_ORG_TO_ORG_PROVIDER_SUBSCRIPTIONS_SUCCESS,
  FETCH_ORG_TO_ORG_CONSUMER_SUBSCRIPTIONS_SUCCESS,
  DELETE_TEAM_MEMBER_FROM_STATE,
  FETCH_ORG_CONTACTS_SUCCESS,
  SET_BUYER_ORG_DATA,
  DELETE_CONTACT_FROM_STATE,
  SET_COUNTRY_CODES,
  SET_URL_PARAMS,
  SET_ERROR,
  SET_LOADING,
  SET_BACKGROUND_PRINT_JOB_LOADING,
  ADDED_ORG_CONTACT_SUCCESS,
  MODIFIED_ORG_CONTACT_SUCCESS,
  REMOVED_ORG_CONTACT_SUCCESS,
  ORG_CONTACTS_EMPTY,
  ADDED_ORG_TEAM_MEMBER_SUCCESS,
  MODIFIED_ORG_TEAM_MEMBER_SUCCESS,
  REMOVED_ORG_TEAM_MEMBER_SUCCESS,
  FETCH_SPECIES_DATA_SUCCESS,
  FETCH_ACTIVITY_DATA_SUCCESS,
  FETCH_SUBSCRIPTION_ADD_ONS_SUCCESS,
  FETCH_EXTERNAL_WMS_LAYERS_SUCCESS,
  FETCH_EXTERNAL_ORG_DATA_LAYERS_SUCCESS,
  FETCH_EXTERNAL_REST_LAYERS_SUCCESS,
  ADDED_SPECIES_SCHEME,
  MODIFIED_SPECIES_SCHEME,
  REMOVED_SPECIES_SCHEME,
  SET_SELECTED_SPECIES_SCHEME_ID,
  SET_CURRENT_AI_ASSISTANT_THREAD_ID,
  SET_CURRENT_AI_ASSISTANT_MESSAGES,
  SET_OPEN_AI_CHAT_WINDOW,
} from "./actions";

// types
import { AuthProps, AuthActionProps } from "types/auth";

// initial state
export const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  googlePlacesAutocompleteLoaded: false,
  childrenInitialized: false,
  currentSoftwareVersion: "",
  countryCodes: undefined,
  loading: false,
  error: null,
  user: null,
  userOnboardingObject: null,
  userOrg: null,
  orgPrivateData: {},
  orgToOrgProviderSubscriptions: null,
  orgTeam: {},
  orgContacts: {},
  buyerOrgsById: {},
  urlParams: null,
  species: {},
  activityTypes: {},
  speciesSchemes: null,
  selectedSpeciesSchemeId: null,
  subscriptionAddOns: null,
  externalWMSLayers: null,
  externalOrgDataLayers: null,
  externalRESTLayers: null,
  backgroundPrintJobLoading: false,
  aiAssistant: {
    currentThreadId: null,
    currentMessages: [],
    openAiChatWindow: false,
  },
};

// ==============================|| AUTH REDUCER ||============================== //

const auth = (state: AuthProps = initialState, action: AuthActionProps): AuthProps => {
  switch (action.type) {
    // case AUTH_RESET_STATE: return initialState;
    case REGISTER: {
      const { user } = action.payload!;
      return {
        ...state,
        user,
      };
    }
    case LOGIN: {
      const { user } = action.payload!;
      const sameMembership = Boolean(
        user?.id && user?.orgId &&
        user.id === state.user?.id && user.orgId === state.user?.orgId,
      );
      // Profile snapshots do not contain the role; membership data owns it.
      const orgPrivateData = sameMembership ? state.orgPrivateData : {};
      return {
        ...state,
        isLoggedIn: true,
        // isInitialized: true,
        user: user ? {
          ...user,
          orgRole: orgPrivateData?.roles?.[user.id]?.role ?? "not-admin",
        } : user,
        orgPrivateData,
      };
    }
    case IS_INITIALIZED: {
      return {
        ...state,
        isInitialized: true,
      };
    }
    case CHILDREN_IS_INITIALIZED: {
      return {
        ...state,
        childrenInitialized: true,
      };
    }
    case GOOGLE_PLACES_AUTOCOMPLETE_LOADED: {
      return {
        ...state,
        googlePlacesAutocompleteLoaded: true,
      };
    }
    case SET_CURRENT_SOFTWARE_VERSION: {
      const { currentSoftwareVersion } = action.payload!;
      return {
        ...state,
        currentSoftwareVersion: currentSoftwareVersion,
      };
    }
    case LOGOUT: {
      return {
        ...initialState,
        isInitialized: true,
        childrenInitialized: true,
        googlePlacesAutocompleteLoaded: state.googlePlacesAutocompleteLoaded,
        currentSoftwareVersion: state.currentSoftwareVersion,
        urlParams: state.urlParams,
      };
    }
    // case LOGOUT: {
    //   return {
    //     ...state,
    //     isInitialized: true,
    //     childrenInitialized: true,
    //     isLoggedIn: false,
    //     userOrg: null,
    //     user: null,
    //     orgTeam: {},
    //     orgContacts: {},
    //     orgPrivateData: {},
    //     // urlParams: null,
    //     cloudFunctionLoading: false,
    //     cloudFunctionError: null,
    //   };
    // }
    case FETCH_USER_ONBOARDING_OBJECT_SUCCESS: {
      const { userOnboardingObject } = action.payload!;
      return {
        ...state,
        userOnboardingObject: userOnboardingObject,
      };
    }
    case FETCH_USER_ORG_SUCCESS: {
      const { userOrg } = action.payload!;
      return {
        ...state,
        loading: false,
        userOrg: userOrg,
      };
    }

    case FETCH_ORG_PRIVATE_DATA_SUCCESS: {
      const { orgPrivateData, orgId } = action.payload!;
      if (!state.user || !orgId || state.user.orgId !== orgId) return state;
      const updatedUser = { ...state.user };
      updatedUser.orgRole = orgPrivateData?.roles?.[state.user.id]?.role ?? "not-admin";
      return {
        ...state,
        user: updatedUser,
        orgPrivateData: orgPrivateData,
      };
    }

    case FETCH_PROVIDER_ORG_TO_ORG_SUBSCRIPTION_TYPES_SUCCESS: {
      const { providerOrgToOrgSubscriptionTypes } = action.payload!;
      return {
        ...state,
        providerOrgToOrgSubscriptionTypes: providerOrgToOrgSubscriptionTypes,
      };
    }

    case FETCH_ORG_TO_ORG_PROVIDER_SUBSCRIPTIONS_SUCCESS: {
      const { orgToOrgProviderSubscriptions } = action.payload!;
      return {
        ...state,
        orgToOrgProviderSubscriptions: orgToOrgProviderSubscriptions,
      };
    }

    case FETCH_ORG_TO_ORG_CONSUMER_SUBSCRIPTIONS_SUCCESS: {
      const { orgToOrgConsumerSubscriptions } = action.payload!;
      return {
        ...state,
        orgToOrgConsumerSubscriptions: orgToOrgConsumerSubscriptions,
      };
    }

    case ADDED_ORG_TEAM_MEMBER_SUCCESS: {
      const memberData = action.payload!.memberData;
      let udpatedOrgTeam = { ...state.orgTeam };
      // Get member role
      let orgRole = "not-admin";
      if (state.orgPrivateData.roles && state.orgPrivateData.roles[memberData.id]) {
        orgRole = state.orgPrivateData.roles[memberData.id].role;
      }
      let name = memberData.firstName + " " + memberData.lastName;
      udpatedOrgTeam = {
        ...udpatedOrgTeam,
        [memberData.id]: { ...memberData, name: name !== " " ? name : "", orgRole },
      };
      return {
        ...state,
        orgTeam: udpatedOrgTeam,
      };
    }
    case MODIFIED_ORG_TEAM_MEMBER_SUCCESS: {
      const memberData = action.payload!.memberData;
      let udpatedOrgTeam = { ...state.orgTeam };
      // Get member role
      let orgRole = "not-admin";
      if (state.orgPrivateData.roles && state.orgPrivateData.roles[memberData.id]) {
        orgRole = state.orgPrivateData.roles[memberData.id].role;
      }
      let name = memberData.firstName + " " + memberData.lastName;
      udpatedOrgTeam[memberData.id] = { ...memberData, name: name !== " " ? name : "", orgRole };
      return {
        ...state,
        orgTeam: udpatedOrgTeam,
      };
    }
    case REMOVED_ORG_TEAM_MEMBER_SUCCESS: {
      const memberData = action.payload!.memberData;
      let udpatedOrgTeam = { ...state.orgTeam };
      delete udpatedOrgTeam[memberData.id];
      return {
        ...state,
        orgTeam: udpatedOrgTeam,
      };
    }

    case DELETE_TEAM_MEMBER_FROM_STATE: {
      const { removeUserId } = action.payload!;
      if (!removeUserId) return state;
      const udpatedOrgTeam = { ...state.orgTeam };
      delete udpatedOrgTeam[removeUserId];
      return {
        ...state,
        orgTeam: udpatedOrgTeam,
      };
    }
    case ADDED_ORG_CONTACT_SUCCESS: {
      const contactData = action.payload!.contactData;
      let udpatedOrgContacts = { ...state.orgContacts };
      // Add data
      udpatedOrgContacts = { ...udpatedOrgContacts, [contactData.id]: contactData };
      return {
        ...state,
        orgContacts: udpatedOrgContacts,
      };
    }
    case MODIFIED_ORG_CONTACT_SUCCESS: {
      const contactData = action.payload!.contactData;
      let udpatedOrgContacts = { ...state.orgContacts };
      udpatedOrgContacts[contactData.id] = contactData;
      return {
        ...state,
        orgContacts: udpatedOrgContacts,
      };
    }
    case REMOVED_ORG_CONTACT_SUCCESS: {
      const contactData = action.payload!.contactData;
      let udpatedOrgContacts = { ...state.orgContacts };
      delete udpatedOrgContacts[contactData.id];
      return {
        ...state,
        orgContacts: udpatedOrgContacts,
      };
    }
    case ORG_CONTACTS_EMPTY: {
      return {
        ...state,
        orgContacts: {},
      };
    }
    case FETCH_ORG_CONTACTS_SUCCESS: {
      const { orgContacts } = action.payload!;
      return {
        ...state,
        loading: false,
        orgContacts: orgContacts,
      };
    }
    case SET_BUYER_ORG_DATA: {
      const { orgId, orgData } = action.payload!;
      return {
        ...state,
        buyerOrgsById: {
          ...state.buyerOrgsById,
          [orgId]: orgData,
        },
      };
    }
    case DELETE_CONTACT_FROM_STATE: {
      const { removeUserId } = action.payload!;
      if (!removeUserId) return state;
      const udpatedOrgContacts = { ...state.orgContacts };
      delete udpatedOrgContacts[removeUserId];
      return {
        ...state,
        orgContacts: udpatedOrgContacts,
      };
    }
    case SET_COUNTRY_CODES: {
      const { countryCodes } = action.payload!;
      return {
        ...state,
        countryCodes: countryCodes,
      };
    }
    case SET_URL_PARAMS: {
      const { urlParams } = action.payload!;
      return {
        ...state,
        urlParams: urlParams,
      };
    }
    case SET_SELECTED_SPECIES_SCHEME_ID: {
      const { selectedSpeciesSchemeId } = action.payload!;
      return {
        ...state,
        selectedSpeciesSchemeId: selectedSpeciesSchemeId,
      };
    }
    case FETCH_SPECIES_DATA_SUCCESS: {
      const { species } = action.payload!;
      return {
        ...state,
        species: species,
      };
    }

    case ADDED_SPECIES_SCHEME: {
      const speciesScheme = action.payload!;
      const schemeId = speciesScheme.id;
      let updatedSchemes = { ...state.speciesSchemes };
      updatedSchemes = { ...updatedSchemes, [schemeId]: speciesScheme };
      // Add data
      return {
        ...state,
        speciesSchemes: updatedSchemes,
      };
    }

    case MODIFIED_SPECIES_SCHEME: {
      const speciesScheme = action.payload!;
      const schemeId = speciesScheme.id;
      let updatedSchemes = { ...state.speciesSchemes };
      updatedSchemes = { ...updatedSchemes, [schemeId]: speciesScheme };
      return {
        ...state,
        speciesSchemes: updatedSchemes,
      };
    }

    case REMOVED_SPECIES_SCHEME: {
      const schemeId = action.payload!;
      let updatedSchemes = { ...state.speciesSchemes };
      delete updatedSchemes[schemeId];
      return {
        ...state,
        speciesSchemes: updatedSchemes,
      };
    }

    case FETCH_ACTIVITY_DATA_SUCCESS: {
      const { activityTypes } = action.payload!;
      return {
        ...state,
        activityTypes: activityTypes,
      };
    }
    case FETCH_SUBSCRIPTION_ADD_ONS_SUCCESS: {
      const { subscriptionAddOns } = action.payload!;
      return {
        ...state,
        subscriptionAddOns: subscriptionAddOns,
      };
    }
    case FETCH_EXTERNAL_WMS_LAYERS_SUCCESS: {
      const { externalWMSLayers } = action.payload!;
      // const newLayers = {...state.externalWMSLayers, ...externalWMSLayers};
      const newLayers = externalWMSLayers;
      return {
        ...state,
        externalWMSLayers: newLayers,
      };
    }
    case FETCH_EXTERNAL_ORG_DATA_LAYERS_SUCCESS: {
      const { externalOrgDataLayers } = action.payload!;
      // const newLayers = {...state.externalOrgDataLayers, ...externalOrgDataLayers};
      const newLayers = externalOrgDataLayers;
      return {
        ...state,
        externalOrgDataLayers: newLayers,
      };
    }
    case FETCH_EXTERNAL_REST_LAYERS_SUCCESS: {
      const { externalRESTLayers } = action.payload!;
      // const newLayers = {...state.externalRESTLayers, ...externalRESTLayers};
      const newLayers = externalRESTLayers;
      return {
        ...state,
        externalRESTLayers: newLayers,
      };
    }
    case SET_BACKGROUND_PRINT_JOB_LOADING: {
      const { backgroundPrintJobLoading } = action.payload!;
      return {
        ...state,
        backgroundPrintJobLoading: backgroundPrintJobLoading,
      };
    }
    case SET_CURRENT_AI_ASSISTANT_THREAD_ID: {
      const { currentThreadId } = action.payload!;
      return {
        ...state,
        aiAssistant: {
          ...state.aiAssistant,
          currentThreadId: currentThreadId,
        },
      };
    }
    case SET_CURRENT_AI_ASSISTANT_MESSAGES: {
      const { messages } = action.payload!;
      return {
        ...state,
        aiAssistant: {
          ...state.aiAssistant,
          currentMessages: [...messages],
        },
      };
    }
    case SET_OPEN_AI_CHAT_WINDOW: {
      const { openAiChatWindow } = action.payload!;
      return {
        ...state,
        aiAssistant: {
          ...state.aiAssistant,
          openAiChatWindow: openAiChatWindow,
        },
      };
    }

    case SET_LOADING: {
      const { loading } = action.payload!;
      return {
        ...state,
        loading: loading,
      };
    }
    case SET_ERROR: {
      const { error } = action.payload!;
      return {
        ...state,
        error: error,
        loading: false,
      };
    }
    default: {
      return { ...state };
    }
  }
};

export default auth;
